import type { Cobertura, DadosDoPainel, Habilitacao, Indicadores } from "./tipos";

/**
 * Os dados que o painel mostra enquanto não existe módulo de negócio nenhum.
 *
 * <h2>Por que eles existem</h2>
 *
 * Decisão do PO: uma tela povoada com exemplos realistas ajuda a levantar
 * requisitos muito mais que uma tela vazia. Olhar para um número concreto e
 * dizer "não é isso que eu preciso ver" é trabalho de especificação.
 *
 * <h2>🔴 Por que eles NÃO PODEM chegar à operação</h2>
 *
 * O sistema está na internet e vai receber dado real. Um painel de risco
 * regulatório com números inventados, lido por quem não sabe que são
 * inventados, é pior que um painel vazio: ele produz decisão errada com
 * aparência de fundamento.
 *
 * <p>A faixa de aviso na tela é a defesa <b>fraca</b> — ela some com uma regra
 * de CSS, um recorte de captura de tela, uma impressão mal configurada.
 *
 * <p>A defesa que vale é esta função: em {@code operacao_assistida} e
 * {@code producao} ela devolve <b>vazio</b>. Não há dado a marcar, não há
 * marcação a falhar, e ninguém precisa lembrar de desligar nada no dia em que o
 * ambiente mudar.
 *
 * <p>É o mesmo princípio da política de senha relaxada: <b>o controle é a
 * ausência, não o aviso.</b>
 */

/** Ambientes que recebem — ou vão receber — dado de verdade. */
const AMBIENTES_COM_DADO_REAL = ["operacao_assistida", "producao"];

const HABILITACOES: Habilitacao[] = [
  {
    id: "alvara-prolab",
    tipo: "Alvará sanitário",
    titular: "prolab-aromatique",
    situacao: "vencendo",
    diasParaVencer: 87,
    acao: "Protocolar a renovação — ainda não aberta.",
    // O caso que o painel existe para tornar visível.
    quemDepende: ["prolab-aromatique", "plantus-oleos-extratos"],
  },
  {
    id: "contrato-locacao",
    tipo: "Contrato de locação de espaço",
    titular: "prolab-aromatique",
    situacao: "vencendo",
    diasParaVencer: 143,
    acao: "Confirmar renovação com a ProLab antes do alvará.",
    quemDepende: ["plantus-oleos-extratos"],
  },
  {
    id: "afe-plantus-oleos",
    tipo: "AFE",
    titular: "plantus-oleos-extratos",
    situacao: "emObtencao",
    diasParaVencer: null,
    acao: "Acompanhar o protocolo — a linha de IFAV não opera até sair.",
    quemDepende: ["plantus-oleos-extratos"],
  },
  {
    id: "afe-prolab",
    tipo: "AFE",
    titular: "prolab-aromatique",
    situacao: "vigente",
    diasParaVencer: 412,
    acao: "Nada a fazer.",
    quemDepende: ["prolab-aromatique"],
  },
  {
    id: "afe-plantus-global",
    tipo: "AFE de distribuição",
    titular: "plantus-global",
    situacao: "vigente",
    diasParaVencer: 298,
    acao: "Nada a fazer.",
    quemDepende: ["plantus-global"],
  },
];

const INDICADORES: Indicadores = {
  regularizacoesVencendo: 2,
  isentosSemFicha: 31,
  fichasVencidas: 4,
  fichasARevisar: 9,
  exigenciasComPrazo: 1,
  peticoesParadas: 3,
};

const COBERTURA: Cobertura[] = [
  { empresa: "plantus-oleos-extratos", produtosCadastrados: 47, produtosDeclarados: 250 },
  { empresa: "prolab-aromatique", produtosCadastrados: 12, produtosDeclarados: 40 },
  { empresa: "plantus-global", produtosCadastrados: 0, produtosDeclarados: 30 },
];

const VAZIO: DadosDoPainel = {
  saoExemplo: false,
  habilitacoes: [],
  indicadores: {
    regularizacoesVencendo: 0,
    isentosSemFicha: 0,
    fichasVencidas: 0,
    fichasARevisar: 0,
    exigenciasComPrazo: 0,
    peticoesParadas: 0,
  },
  cobertura: [],
};

/**
 * O que o painel exibe, dado o ambiente.
 *
 * @param ambiente vem de `GET /api/versao`. **`null` é tratado como ambiente
 *        com dado real** — na dúvida, não inventa. É a mesma direção segura da
 *        `FaixaAmbiente`, que na dúvida avisa.
 */
export function dadosDoPainel(ambiente: string | null): DadosDoPainel {
  if (ambiente === null || AMBIENTES_COM_DADO_REAL.includes(ambiente)) {
    return VAZIO;
  }
  return {
    saoExemplo: true,
    habilitacoes: HABILITACOES,
    indicadores: INDICADORES,
    cobertura: COBERTURA,
  };
}
